import React, { useState, useRef, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Box, 
  CircularProgress, 
  Divider,
  List,
  ListItem,
  makeStyles,
  FormControlLabel,
  Checkbox,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Description, CloudUpload, ArrowRight, GetApp, Print as PrintIcon, ExpandMore, ExpandLess } from '@material-ui/icons';
import { useApiKey } from '../context/ApiKeyContext';
import { v4 as uuidv4 } from 'uuid';

// URL base da API - usa variável de ambiente ou fallback para produção
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  processingBox: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  fileInput: {
    display: 'none',
  },
  uploadButton: {
    marginLeft: theme.spacing(1),
  },
  fileName: {
    marginLeft: theme.spacing(1),
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resultContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  resultHeader: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: theme.spacing(1),
  },
  resultSubHeader: {
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textAlign: 'left',
  },
  resultItem: {
    marginBottom: theme.spacing(1),
    textAlign: 'left',
  },
  resultList: {
    textAlign: 'left',
    paddingTop: theme.spacing(1),
  },
  listBullet: {
    display: 'inline-block',
    // marginRight: theme.spacing(0.5),
    fontSize: '12px',
    color: theme.palette.primary.main,
  },
}));

const DocumentProcessor = ({ templateData }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  
  // Usar a API Key do contexto global
  const { apiKey } = useApiKey();
  const [externalId, setExternalId] = useState('');
  const [autoGenExternalId, setAutoGenExternalId] = useState(true);
  const [cliente, setCliente] = useState('');
  const [ambiente, setAmbiente] = useState('DEV');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [rawResult, setRawResult] = useState(null);
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [showEmptyItems, setShowEmptyItems] = useState(false);
  const [showRawResult, setShowRawResult] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Função para alternar o estado de expansão de um nó
  const toggleNode = (nodePath) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodePath)) {
        newSet.delete(nodePath);
      } else {
        newSet.add(nodePath);
      }
      return newSet;
    });
  };

  // Função para verificar se um nó está expandido
  const isNodeExpanded = (nodePath) => {
    // Se o nó não está no Set, significa que ainda não foi clicado
    // Nesse caso, retornamos true para que todos comecem expandidos
    return !expandedNodes.has(nodePath);
  };

  // Função para diagnóstico do template
  useEffect(() => {
    if (templateData) {
      console.log('➡️ Template atualizado no DocumentProcessor:', templateData);
      console.log('➡️ Tipo:', typeof templateData);
      console.log('➡️ Número de chaves:', Object.keys(templateData).length);
      console.log('➡️ Chaves:', Object.keys(templateData));
    } else {
      console.log('➡️ Template é null/undefined no DocumentProcessor');
    }
  }, [templateData]);

  // Função para formatar o template para o formato correto da API
  const formatTemplateForAPI = (template) => {
    // Se não houver template, retorna um objeto vazio
    if (!template) return {};
    
    // Log do template original para diagnóstico
    console.log('Template original recebido (objeto):', template);
    console.log('Template original recebido (string):', JSON.stringify(template));
    console.log('Template tem chaves?', Object.keys(template).length);
    
    try {
      // Faz uma cópia profunda do objeto para não modificar o original
      const formattedTemplate = JSON.parse(JSON.stringify(template));
      
      // Verificamos se o template parece ter o formato correto (pelo menos uma chave)
      if (Object.keys(formattedTemplate).length === 0) {
        console.error('O template parece estar vazio. Verifique se foi criado corretamente.');
        throw new Error('Template vazio ou inválido');
      }
      
      // Verificação final do formato
      console.log('Template formatado para API:', formattedTemplate);
      
      return formattedTemplate;
    } catch (error) {
      console.error('Erro ao formatar template:', error);
      throw new Error('Erro ao processar o template: ' + error.message);
    }
  };

  const handleFileSelect = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const validateInputs = () => {
    if (!apiKey) {
      setError('API Key não encontrada. Por favor, faça login novamente.');
      return false;
    }
    if (!externalId.trim() && !autoGenExternalId) {
      setError('O External ID é obrigatório');
      return false;
    }
    if (!cliente.trim()) {
      setError('O Cliente é obrigatório');
      return false;
    }
    if (!selectedFile) {
      setError('Por favor, selecione um arquivo PDF para processamento');
      return false;
    }
    // Verificar se o template é válido (não está vazio)
    if (!templateData) {
      console.error('Template é null/undefined');
      setError('É necessário criar um template válido antes de processar o documento');
      return false;
    }
    
    console.log('Validando template:', templateData);
    console.log('Quantidade de chaves:', Object.keys(templateData).length);
    
    if (Object.keys(templateData).length === 0) {
      console.error('Template está vazio (sem chaves)');
      setError('É necessário criar um template válido antes de processar o documento');
      return false;
    }
    return true;
  };

  const processDocument = async () => {
    // Validar entradas antes de processar
    if (!validateInputs()) return;

    // Preparar o external ID (gerado ou inserido pelo usuário)
    let idToUse = externalId;
    
    // Gerar um UUID se a opção estiver habilitada
    if (autoGenExternalId) {
      idToUse = uuidv4();
      setExternalId(idToUse); // Atualiza o campo visual, mas não esperamos que seja processado a tempo
    }

    setError(null);
    setIsProcessing(true);
    setProcessingStatus('Iniciando processamento...');
    setProcessingProgress(10);
    setResult(null);

    try {
      // Preparar os dados para a API
      // Mudando a abordagem para usar um JSON direto em vez de FormData
      const formData = new FormData();
      formData.append('arquivo_pdf', selectedFile);
      
      // Preparar o template JSON
      let jsonData;
      
      try {
        // Tenta formatar o template - agora pode lançar uma exceção se inválido
        jsonData = formatTemplateForAPI(templateData);
      } catch (templateError) {
        setError(templateError.message);
        setIsProcessing(false);
        return;
      }
      
      // Verificação final - não enviar se o JSON estiver vazio
      if (JSON.stringify(jsonData) === '{}' || JSON.stringify(jsonData) === '[]') {
        setError('O template está vazio. Por favor, crie um template válido antes de processar.');
        setIsProcessing(false);
        return;
      }
      
      // Adicionamos cliente e ambiente como campos separados no FormData (não dentro do JSON)
      formData.append('ambiente', ambiente);
      formData.append('cliente', cliente);
      
      // Convertemos o template para uma string JSON e adicionamos ao FormData
      formData.append('data', JSON.stringify(jsonData));
      
      // Para diagnóstico detalhado
      console.log('JSON data sendo enviado (objeto):', jsonData);
      console.log('JSON data sendo enviado (string):', JSON.stringify(jsonData));
      console.log('Cliente:', cliente);
      console.log('Ambiente:', ambiente);
      console.log('External ID sendo usado:', idToUse);

      // Configurar URL com parâmetros - usando o ID preparado, não o estado React
      const url = `${API_BASE_URL}/doc_reader/process_digital_pdf?external_id=${encodeURIComponent(idToUse)}&precision=HIGH&pre_processing=OCR`;

      // Verificar o conteúdo do formData antes de enviar
      // Isso é apenas para diagnóstico e pode ser removido depois
      for (let pair of formData.entries()) {
        console.log(`FormData - ${pair[0]}: `, pair[1]);
      }

      // Fazer a primeira chamada para iniciar o processamento
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'api_key': apiKey,
          // Não definimos o Content-Type explicitamente pois o browser
          // o ajustará automaticamente com o boundary correto para FormData
        },
        body: formData
      });

      if (!response.ok) {
        // Tentar obter mais detalhes do erro
        try {
          const errorData = await response.json();
          throw new Error(`Erro na requisição: ${response.status} ${response.statusText}. Detalhes: ${JSON.stringify(errorData)}`);
        } catch (parseError) {
          throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      if (!data.job_id) {
        throw new Error('Resposta inválida: job_id não encontrado');
      }

      const jobId = data.job_id;
      console.log(`Job iniciado com sucesso! Job ID: ${jobId}`);
      setProcessingStatus(`Processando documento... (Job ID: ${jobId})`);
      setProcessingProgress(30);

      // Iniciar polling para verificar o status do job
      await checkJobStatus(jobId);

    } catch (error) {
      console.error('Erro ao processar documento:', error);
      setError(`Falha ao processar o documento: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const checkJobStatus = async (jobId) => {
    let isDone = false;
    let attempts = 0;
    let maxProgressValue = 90; // Progresso máximo antes de concluir
    
    // Resetar o estado de cancelamento
    setCancelProcessing(false);

    while (!isDone) {
      // Verificar se o usuário cancelou o processamento
      if (cancelProcessing) {
        console.log('Processamento cancelado pelo usuário.');
        setProcessingStatus('Processamento cancelado pelo usuário.');
        setIsProcessing(false);
        return;
      }
      
      try {
        // Esperar entre as verificações, aumentando o tempo de espera gradualmente
        if (attempts > 0) {
          const waitTime = Math.min(5000 + (attempts * 1000), 15000); // Aumenta o tempo de espera, máximo de 15 segundos
          console.log(`Aguardando ${waitTime/1000} segundos antes da próxima verificação...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
        
        attempts++;
        // Calcula o progresso gradual, mas sem atingir 100% até concluir
        const progressValue = 30 + Math.min(attempts * 2, maxProgressValue - 30);
        setProcessingProgress(progressValue);
        
        // Passar o job_id como parâmetro na URL conforme especificação da API
        const url = `${API_BASE_URL}/job/job_result?job_id=${encodeURIComponent(jobId)}`;
        
        // Log para diagnóstico
        console.log(`Verificando status do job ${jobId} (tentativa ${attempts})...`);
        console.log(`URL: ${url}`);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'api_key': apiKey,
            'Content-Type': 'application/json'
          },
          // Corpo vazio, pois o job_id agora está na URL
          body: ''
        });

        // Tratamento especial para códigos de erro específicos que indicam falha permanente
        if (response.status === 404) {
          throw new Error(`Job não encontrado (404). O job pode ter expirado ou não existir.`);
        }
        
        if (response.status === 401 || response.status === 403) {
          throw new Error(`Erro de autenticação (${response.status}). Verifique sua API Key.`);
        }

        if (!response.ok) {
          // Para outros códigos de erro, tentar obter detalhes mas continuar tentando
          console.error(`Erro na tentativa ${attempts}: ${response.status} ${response.statusText}`);
          
          try {
            const errorData = await response.json();
            console.error(`Detalhes do erro:`, errorData);
          } catch (parseError) {
            console.error(`Não foi possível obter detalhes do erro.`);
          }
          
          // Mostrar erro temporário na interface, mas continuar tentando
          setProcessingStatus(`Erro temporário na verificação. Tentando novamente... (Tentativa ${attempts})`);
          continue; // Continua o loop sem lançar exceção
        }

        const data = await response.json();
        setProcessingStatus(`Verificando status do processamento... (Tentativa ${attempts})`);

        if (data.status === 'DONE') {
          isDone = true;
          setProcessingProgress(100);
          setProcessingStatus('Processamento concluído!');
          
          // Salvar os resultados separadamente
          setResult(data.result);
          setRawResult(data.raw_result || null);
        } else if (data.status === 'ERROR') {
          throw new Error(`Erro no processamento: ${data.error || 'Erro desconhecido'}`);
        } else if (data.status === 'PROCESSING') {
          // Continua no loop, ainda processando
          setProcessingStatus(`Documento ainda em processamento... (Tentativa ${attempts})`);
        } else {
          // Status desconhecido, mas continua tentando
          console.warn(`Status desconhecido recebido: ${data.status}`);
          //setProcessingStatus(`Status do processamento: ${data.status} (Tentativa ${attempts})`);
          setProcessingStatus(`Status do processamento: ${data.status}`);
        }
      } catch (error) {
        console.error('Erro ao verificar status do job:', error);
        
        // Apenas erros críticos que realmente impedem o funcionamento farão o processo parar
        if (error.message.includes('404') || 
            error.message.includes('401') || 
            error.message.includes('403') ||
            error.message.includes('Erro no processamento')) {
          setError(`Falha ao verificar status do processamento: ${error.message}`);
          setIsProcessing(false);
          return;
        }
        
        // Para outros erros, mostra mensagem mas continua tentando
        setProcessingStatus(`Erro temporário: ${error.message}. Tentando novamente... (Tentativa ${attempts})`);
        console.log('Continuando tentativas apesar do erro...');
      }
    }

    setIsProcessing(false);
  };

  // Função para obter o label de um item, ou usar o nome como fallback
  const getDisplayLabel = (key, value) => {
    // Se o valor for um objeto e tiver um campo 'label', use-o
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (value.label) {
        return value.label;
      }
      
      // Verificar se o próprio valor tem uma key especial 'label' no nível superior
      // (pode ocorrer em templates específicos da API)
      if (Object.keys(value).includes('label') && typeof value.label === 'string') {
        return value.label;
      }
    }
    
    // Tentativa de formatar o nome da chave para exibição mais amigável
    // Transforma snake_case ou camelCase em palavras separadas com primeira letra maiúscula
    const formatted = key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^\w/, (c) => c.toUpperCase());
    
    return formatted;
  };

  // Função recursiva para renderizar os resultados em formato adequado
  const renderResultItem = (key, value, depth = 0, path = '') => {
    const nodePath = path ? `${path}.${key}` : key;
    const isExpanded = isNodeExpanded(nodePath);
    
    // Obter o label para exibição
    const displayLabel = getDisplayLabel(key, value);
    
    // Definir o espaçamento para níveis hierárquicos
    const indentSize = depth * 16; // Mantido em 16px por nível para não ficar excessivo
    
    // Função auxiliar para renderizar o título do item com estilo consistente
    const renderItemTitle = (showValue = null) => {
      if (depth === 0) {
        // Estilo destacado para todos os itens de nível raiz
        return (
          <>
            <Typography variant="h6" className={classes.resultHeader} align="left">
              {displayLabel}
            </Typography>
            {showValue !== null && (
              <Typography 
                variant="body1" 
                style={{ 
                  marginTop: -4, 
                  marginBottom: 12, 
                  paddingLeft: 8,
                  color: '#555',
                  fontStyle: showValue.startsWith('Tipo:') || showValue.startsWith('Lista com') ? 'italic' : 'normal'
                }}
              >
                {showValue}
              </Typography>
            )}
          </>
        );
      } else {
        // Estilo normal para os demais níveis
        return (
          <Typography variant="subtitle1" className={classes.resultSubHeader} style={{ textAlign: 'left' }} align="left">
            {displayLabel}
          </Typography>
        );
      }
    };

    // Função auxiliar para renderizar o conteúdo de um item
    const renderItemContent = (content) => {
      return (
        <Box ml={2} style={{ textAlign: 'left', borderLeft: '1px solid #e0e0e0', paddingLeft: 8 }}>
          {content}
        </Box>
      );
    };
    
    // Renderizar diferentes tipos de valores
    if (value === null || value === undefined) {
      // Se não devemos mostrar itens vazios e não é um item raiz, retornar null
      if (!showEmptyItems && depth > 0) {
        return null;
      }
      
      return (
        <Box key={key} className={classes.resultItem} style={{ 
          paddingLeft: indentSize, 
          textAlign: 'left',
          marginBottom: depth === 0 ? 24 : 8 // Margem inferior maior para itens raiz
        }}>
          {depth === 0 ? renderItemTitle() : (
            <Typography variant="body2" align="left">
              <strong>{displayLabel}:</strong> <em>Não definido</em>
            </Typography>
          )}
        </Box>
      );
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // É um objeto
      // Determinar quais campos não mostrar diretamente (campos de metadados)
      const metadataFields = ['label', 'type', 'description'];
      
      // Se for um objeto que tem apenas os campos de metadados, mostrar diferente
      const keys = Object.keys(value);
      const hasOnlyMetadata = keys.every(k => metadataFields.includes(k));
      
      if (hasOnlyMetadata && keys.length > 0) {
        // Mostrar os metadados como uma descrição
        let description = '';
        if (value.description) {
          description = value.description;
        } else if (value.type) {
          description = `Tipo: ${value.type}`;
        }
        
        return (
          <Box key={key} className={classes.resultItem} style={{ 
            paddingLeft: indentSize, 
            textAlign: 'left',
            marginBottom: depth === 0 ? 24 : 8 // Margem inferior maior para itens raiz
          }}>
            {depth === 0 ? renderItemTitle(description ? description : null) : (
              <Typography variant="body2" align="left">
                <strong>{displayLabel}:</strong>
                {description && (
                  <em style={{ marginLeft: 8, color: 'gray' }}>{description}</em>
                )}
              </Typography>
            )}
          </Box>
        );
      }
      
      // Se não é um objeto que tem apenas metadados, renderizar normalmente com filhos
      // Vamos verificar se tem algum metadado importante para mostrar
      let metadataInfo = '';
      if (value.type) {
        metadataInfo = `Tipo: ${value.type}`;
      }
      if (value.description) {
        metadataInfo = metadataInfo ? `${metadataInfo}, ${value.description}` : value.description;
      }
      
      // Filtrar os filhos para remover itens vazios se necessário
      const filteredEntries = Object.entries(value)
        .filter(([childKey]) => !metadataFields.includes(childKey) || keys.length === 1)
        .map(([childKey, childValue]) => renderResultItem(childKey, childValue, depth + 1, nodePath))
        .filter(Boolean); // Remove nulls (itens vazios filtrados)
      
      // Se não temos filhos para mostrar e não devemos mostrar itens vazios, retornar null
      if (!showEmptyItems && filteredEntries.length === 0 && depth > 0) {
        return null;
      }
      
      return (
        <Box key={key} style={{ 
          paddingLeft: indentSize, 
          textAlign: 'left',
          marginBottom: depth === 0 ? 24 : 8
        }}>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={() => toggleNode(nodePath)}
              style={{ marginRight: 4, padding: 4 }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            {depth === 0 ? renderItemTitle(metadataInfo ? metadataInfo : null) : (
              <Typography variant="subtitle1" style={{ textAlign: 'left' }} align="left">
                {displayLabel}
              </Typography>
            )}
          </Box>
          <Collapse in={isExpanded}>
            {renderItemContent(filteredEntries)}
          </Collapse>
        </Box>
      );
    } else if (Array.isArray(value)) {
      // É um array
      // Filtrar itens vazios se necessário
      const filteredItems = value.length > 0 ? value.map((item, index) => {
        if (typeof item === 'object' && item !== null) {
          const renderedItem = (
            <ListItem key={index} dense disableGutters style={{ textAlign: 'left', marginBottom: 8 }}>
              <Box display="flex" alignItems="flex-start" style={{ textAlign: 'left', width: '100%' }}>
                <span className={classes.listBullet} style={{ color: '#1976d2', marginRight: 4, marginTop: 3 }}>●</span>
                <Box style={{ textAlign: 'left', width: '100%' }}>
                  {/* Se o item tiver um campo label, usar como título do item */}
                  {item.label && (
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      {item.label}
                    </Typography>
                  )}
                  
                  {/* Filtrar os campos de metadados */}
                  {Object.entries(item)
                    .filter(([itemKey]) => 
                      itemKey !== 'label' && 
                      itemKey !== 'type' && 
                      itemKey !== 'description'
                    )
                    .map(([itemKey, itemValue]) =>
                      renderResultItem(itemKey, itemValue, depth + 2, `${nodePath}[${index}]`)
                    )
                  }
                </Box>
              </Box>
            </ListItem>
          );
          
          // Se não devemos mostrar itens vazios, verificar se o item tem conteúdo
          if (!showEmptyItems) {
            const hasContent = Object.entries(item)
              .filter(([itemKey]) => 
                itemKey !== 'label' && 
                itemKey !== 'type' && 
                itemKey !== 'description'
              )
              .some(([_, itemValue]) => {
                if (itemValue === null || itemValue === undefined) return false;
                if (typeof itemValue === 'object' && !Array.isArray(itemValue)) {
                  return Object.keys(itemValue).length > 0;
                }
                if (Array.isArray(itemValue)) {
                  return itemValue.length > 0;
                }
                return true;
              });
            
            return hasContent ? renderedItem : null;
          }
          
          return renderedItem;
        } else {
          return (
            <ListItem key={index} dense disableGutters style={{ textAlign: 'left' }}>
              <Box display="flex" alignItems="flex-start" style={{ textAlign: 'left', width: '100%' }}>
                <span className={classes.listBullet} style={{ color: '#1976d2', marginRight: 4, marginTop: 3 }}>●</span>
                <Typography variant="body2" align="left">{String(item)}</Typography>
              </Box>
            </ListItem>
          );
        }
      }).filter(Boolean) : null;
      
      // Se não temos itens para mostrar e não devemos mostrar itens vazios, retornar null
      if (!showEmptyItems && (!filteredItems || filteredItems.length === 0) && depth > 0) {
        return null;
      }
      
      return (
        <Box key={key} style={{ 
          paddingLeft: indentSize, 
          textAlign: 'left',
          marginBottom: depth === 0 ? 24 : 8
        }}>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={() => toggleNode(nodePath)}
              style={{ marginRight: 4, padding: 4 }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            {depth === 0 ? renderItemTitle(null) : (
              <Typography variant="subtitle1" style={{ textAlign: 'left' }} align="left">
                <strong>{displayLabel}:</strong>
              </Typography>
            )}
          </Box>
          <Collapse in={isExpanded}>
            <List dense className={classes.resultList} style={{ 
              textAlign: 'left',
              borderLeft: '1px solid #e0e0e0',
              marginLeft: 8,
              paddingLeft: 8
            }}>
              {filteredItems ? (
                filteredItems
              ) : (
                <ListItem dense disableGutters style={{ textAlign: 'left' }}>
                  <Typography 
                    variant="body2" 
                    style={{ fontStyle: 'italic', color: '#666' }}
                  >
                    Nenhum item encontrado
                  </Typography>
                </ListItem>
              )}
            </List>
          </Collapse>
        </Box>
      );
    } else {
      // É um valor primitivo (string, número, booleano)
      return (
        <Box key={key} className={classes.resultItem} style={{ 
          paddingLeft: indentSize, 
          textAlign: 'left',
          marginBottom: depth === 0 ? 24 : 8 // Margem inferior maior para itens raiz
        }}>
          {depth === 0 ? renderItemTitle(String(value)) : (
            <Typography variant="body2" align="left">
              <strong>{displayLabel}:</strong> {String(value)}
            </Typography>
          )}
        </Box>
      );
    }
  };

  // Função para cancelar o processamento
  const handleCancelProcessing = () => {
    setCancelProcessing(true);
    setProcessingStatus('Cancelando processamento...');
    // O cancelamento efetivo acontecerá no próximo ciclo do loop de checkJobStatus
  };

  // Função para baixar os resultados como JSON
  const downloadResultsAsJson = () => {
    if (!result) return;

    try {
      // Determinar qual resultado usar baseado na seleção do usuário
      const dataToDownload = showRawResult && rawResult ? rawResult : result;
      
      // Criar uma string JSON formatada
      const jsonString = JSON.stringify(dataToDownload, null, 2);
      
      // Criar um blob com o conteúdo JSON
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Criar uma URL temporária para o blob
      const url = URL.createObjectURL(blob);
      
      // Criar um elemento de link para o download
      const link = document.createElement('a');
      link.href = url;
      link.download = showRawResult && rawResult ? 'resultado-bruto.json' : 'resultado.json';
      
      // Adicionar o link ao documento, clicar nele e removê-lo
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Liberar a URL do objeto
      URL.revokeObjectURL(url);
      
      console.log('Download do resultado iniciado!');
    } catch (error) {
      console.error('Erro ao baixar resultados como JSON:', error);
      alert('Erro ao gerar o arquivo JSON. Por favor, tente novamente.');
    }
  };

  // Função para imprimir os resultados
  const printResults = () => {
    if (!result) return;

    try {
      // Determinar qual resultado usar baseado na seleção do usuário
      const dataToPrint = showRawResult && rawResult ? rawResult : result;
      
      // Criamos um novo documento para impressão
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert('Por favor, permita pop-ups para imprimir o documento.');
        return;
      }
      
      // Criamos um novo conteúdo HTML para impressão, apenas com os resultados
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>eLaw GenAI - ${showRawResult && rawResult ? 'Resultado Bruto' : 'Resultado Final'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.5;
              padding: 20px;
              color: #333;
            }
            h1 {
              color: #1976d2;
              font-size: 24px;
              margin-bottom: 16px;
              padding-bottom: 8px;
              border-bottom: 2px solid #e0e0e0;
            }
            h2 {
              color: #333;
              font-size: 18px;
              margin-top: 16px;
              margin-bottom: 8px;
            }
            h3 {
              color: #555;
              font-size: 16px;
              margin-top: 12px;
              margin-bottom: 6px;
            }
            .result-item {
              margin-bottom: 12px;
            }
            .list-item {
              margin-bottom: 4px;
              margin-left: 24px;
              position: relative;
            }
            .list-item:before {
              content: "•";
              color: #1976d2;
              position: absolute;
              left: -12px;
            }
            .empty-list {
              font-style: italic;
              color: #777;
              margin-left: 12px;
            }
            .item-label {
              font-weight: bold;
            }
            .item-value {
              margin-left: 8px;
            }
            .parent-item {
              margin-bottom: 16px;
              border-left: 1px solid #e0e0e0;
              padding-left: 12px;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <h1>eLaw GenAI - ${showRawResult && rawResult ? 'Resultado Bruto' : 'Resultado Final'}</h1>
          <div id="print-content">${renderPrintContent(dataToPrint)}</div>
        </body>
        </html>
      `;
      
      // Escrevemos o conteúdo na nova janela
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Aguardamos o conteúdo ser carregado e impressão ser chamada
      printWindow.onload = function() {
        printWindow.print();
        // Fechar a janela após a impressão (opcional - em alguns navegadores o usuário decide)
        printWindow.onafterprint = function() {
          printWindow.close();
        };
      };
    } catch (error) {
      console.error('Erro ao imprimir resultados:', error);
      alert('Erro ao gerar a visualização para impressão. Por favor, tente novamente.');
    }
  };

  // Função auxiliar para gerar o HTML para impressão
  const renderPrintContent = (data) => {
    if (!data || typeof data !== 'object') {
      return '<div class="empty-list">Nenhum resultado disponível</div>';
    }
    
    let html = '';
    
    Object.entries(data).forEach(([key, value]) => {
      html += renderPrintItem(key, value, 0);
    });
    
    return html;
  };

  // Função recursiva para renderizar itens para impressão
  const renderPrintItem = (key, value, depth = 0) => {
    // Obter o label para exibição
    const displayLabel = getDisplayLabel(key, value);
    let html = '';
    
    // Renderizar o item baseado no tipo
    if (value === null || value === undefined) {
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2><div><em>Não definido</em></div>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span> <em>Não definido</em></div>`;
      }
      html += '</div>';
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // É um objeto
      const metadataFields = ['label', 'type', 'description'];
      const keys = Object.keys(value);
      const hasOnlyMetadata = keys.every(k => metadataFields.includes(k));
      
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2>`;
        
        // Mostrar metadados se disponíveis
        if (value.description) {
          html += `<div style="margin-top: -8px; color: #555;">${value.description}</div>`;
        } else if (value.type) {
          html += `<div style="margin-top: -8px; color: #555; font-style: italic;">Tipo: ${value.type}</div>`;
        }
      } else {
        html += `<div><span class="item-label">${displayLabel}</span></div>`;
      }
      
      if (!hasOnlyMetadata) {
        html += '<div class="parent-item">';
        Object.entries(value)
          .filter(([childKey]) => !metadataFields.includes(childKey) || keys.length === 1)
          .forEach(([childKey, childValue]) => {
            html += renderPrintItem(childKey, childValue, depth + 1);
          });
        html += '</div>';
      }
      
      html += '</div>';
    } else if (Array.isArray(value)) {
      // É um array
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span></div>`;
      }
      
      if (value.length === 0) {
        // Array vazio - mostrar mensagem
        html += '<div class="empty-list">Nenhum item encontrado</div>';
      } else {
        // Renderizar itens do array
        value.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            html += '<div class="list-item">';
            
            // Se o item tiver um label, usar como título
            if (item.label) {
              html += `<div style="font-weight: bold;">${item.label}</div>`;
            }
            
            // Renderizar os campos do objeto dentro do item da lista
            Object.entries(item)
              .filter(([itemKey]) => itemKey !== 'label' && itemKey !== 'type' && itemKey !== 'description')
              .forEach(([itemKey, itemValue]) => {
                html += renderPrintItem(itemKey, itemValue, depth + 2);
              });
            
            html += '</div>';
          } else {
            html += `<div class="list-item">${String(item)}</div>`;
          }
        });
      }
      
      html += '</div>';
    } else {
      // É um valor primitivo
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2><div>${String(value)}</div>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span> <span class="item-value">${String(value)}</span></div>`;
      }
      html += '</div>';
    }
    
    return html;
  };

  return (
    <>
      <Paper className={classes.root}>
        <Typography variant="h6" gutterBottom>
          <Box fontWeight="fontWeightBold">
            Processamento de Documento
          </Box>
        </Typography>
        <Divider style={{ marginBottom: 16 }} />
        
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="External ID"
              variant="outlined"
              fullWidth
              className={classes.formField}
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              disabled={autoGenExternalId}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoGenExternalId}
                  onChange={(e) => setAutoGenExternalId(e.target.checked)}
                  color="primary"
                />
              }
              label="Criar um external_id automaticamente"
              style={{ marginLeft: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Cliente (obrigatório)"
              variant="outlined"
              fullWidth
              className={classes.formField}
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              required
              error={isProcessing && !cliente.trim()}
              helperText={isProcessing && !cliente.trim() ? "Cliente é obrigatório" : ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl variant="outlined" fullWidth className={classes.formField}>
              <InputLabel id="ambiente-select-label">Ambiente</InputLabel>
              <Select
                labelId="ambiente-select-label"
                id="ambiente-select"
                value={ambiente}
                onChange={(e) => setAmbiente(e.target.value)}
                label="Ambiente"
              >
                <MenuItem value="DEV">DEV</MenuItem>
                <MenuItem value="POC">POC</MenuItem>
                <MenuItem value="SALES">SALES</MenuItem>
              </Select>
              <FormHelperText>Selecione o ambiente de processamento</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" border={1} borderColor="grey.300" borderRadius={4} p={1}>
              <Description color="action" />
              <input
                ref={fileInputRef}
                className={classes.fileInput}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
              />
              <Typography className={classes.fileName}>
                {selectedFile ? selectedFile.name : 'Nenhum arquivo selecionado'}
              </Typography>
              <Button
                variant="contained"
                color="default"
                className={classes.uploadButton}
                startIcon={<CloudUpload />}
                onClick={triggerFileInput}
                size="small"
              >
                Upload
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={processDocument}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <ArrowRight />}
          >
            {isProcessing ? 'Processando...' : 'Processar Documento'}
          </Button>
        </Box>
        
        {isProcessing && (
          <Box className={classes.processingBox}>
            <CircularProgress variant="determinate" value={processingProgress} size={60} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              {processingStatus}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Por favor, aguarde enquanto processamos seu documento. Não feche esta página.
            </Typography>
            
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleCancelProcessing}
              size="small"
            >
              Cancelar Processamento
            </Button>
          </Box>
        )}
      </Paper>
      
      {result && (
        <Paper className={classes.resultContainer}>
          <Typography variant="h6" gutterBottom align="left">
            <Box fontWeight="fontWeightBold" display="flex" justifyContent="space-between" alignItems="center">
              <span>Resultado do Processamento</span>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showEmptyItems}
                      onChange={(e) => setShowEmptyItems(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Mostrar campos sem informações"
                  style={{ marginRight: 16 }}
                />
                {rawResult && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showRawResult}
                        onChange={(e) => setShowRawResult(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Mostrar resultado bruto"
                    style={{ marginRight: 16 }}
                  />
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={printResults}
                  size="small"
                  style={{ marginRight: 8 }}
                >
                  Imprimir Resultado
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GetApp />}
                  onClick={downloadResultsAsJson}
                  size="small"
                >
                  Salvar resultado JSON
                </Button>
              </Box>
            </Box>
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          
          <Box style={{ textAlign: 'left' }}>
            {Object.entries(showRawResult && rawResult ? rawResult : result).map(([key, value]) => 
              renderResultItem(key, value)
            )}
          </Box>
        </Paper>
      )}
    </>
  );
};

export default DocumentProcessor; 